import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject, Observable, of, forkJoin, Subject } from 'rxjs';
import { map, tap, finalize, shareReplay } from 'rxjs/operators';

import {
    AddOrUpdateJobTypeResult,
    BillableHoursForClientResult,
    BillableHoursForUser,
    BillableHoursForUserResult,
    FilteredJobListResult,
    JiraLinksForJob,
    JiraNameResult,
    Job,
    JobDetailWithActionStatuses,
    JobEditDetails,
    JobReportValues,
    JobSearchQuery,
    JobTimeValuesQuery,
    JobType,
    JobTypeToEditResult,
    JobUpdateResult,
    JobValue,
    JobValueDataForYear,
    UpdateJiraLinksForJob,
    LinkedJob,
    BillableHoursForJobType,
    BillableHoursForJobTypeResult,
    ShortJob,
} from './job.types';
import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import * as _ from 'lodash';
import { TogglService } from '../toggl';
import { ResultBase } from '@intranet/core/types';
import { DatePipe } from '@angular/common';

@Injectable({
    providedIn: 'root',
})
export class JobService {
    private _jobs: BehaviorSubject<Job[]> = new BehaviorSubject([]);
    private _jobsTotalResults: BehaviorSubject<number> = new BehaviorSubject(0);
    private _jobTypes: BehaviorSubject<JobType[]> = new BehaviorSubject([]);
    private _filterQuery: BehaviorSubject<JobSearchQuery> = new BehaviorSubject(
        null
    );

    private _simplehttp: HttpClient;
    private _linkedJobsCache = new Map<number, LinkedJob[]>();
    private _linkedJobsInFlight = new Map<number, Observable<LinkedJob[]>>();

    private _billableStatusChanged$ = new Subject<{ jobId: number; isBillable: boolean }>();
    get billableStatusChanged$() { return this._billableStatusChanged$.asObservable(); }

    // Cache for job owners (all jobs, no client filter) - persists across page navigations
    private _allJobOwnersCache: string[] | null = null;
    private _allJobOwnersInFlight: Observable<{ userIds: string[] }> | null = null;

    // Cache for all live jobs (lightweight) — shared across job-search component instances
    private _allLiveJobsCache: ShortJob[] | null = null;
    private _allLiveJobsInFlight: Observable<ShortJob[]> | null = null;

    // Cache for the names of clients that have archived jobs (lightweight)
    private _archivedClientsCache: string[] | null = null;
    private _archivedClientsInFlight: Observable<string[]> | null = null;

    constructor(
        private _http: HttpClient,
        private _backend: HttpBackend,
        private togglService: TogglService,
        private _datePipe: DatePipe,
        @Inject('BASE_URL') private originUrl: string
    ) {
        this._simplehttp = new HttpClient(this._backend);
    }

    get jobs$(): Observable<Job[]> {
        return this._jobs.asObservable();
    }

    set jobs(value: Job[]) {
        this._jobs.next(value);
    }

    get jobTypes$(): Observable<JobType[]> {
        return this._jobTypes.asObservable();
    }

    get filterQuery$(): Observable<JobSearchQuery> {
        return this._filterQuery.asObservable();
    }

    get filterQuery(): JobSearchQuery {
        return this._filterQuery.value;
    }

    get jobsTotalResults$(): Observable<number> {
        return this._jobsTotalResults.asObservable();
    }

    getJobs(query?: JobSearchQuery): Observable<Job[]> {
        return this._http.get<Job[]>(this._buildSearchQuery(query)).pipe(
            map((response: any) => {
                if (response.success) {
                    // let jobs: any = query.isPaginating ? this._jobs.value : [];
                    // response.jobs.forEach((data) =>
                    //     jobs.push(this._mapJob(data))
                    // );

                    // Cache for future use
                    this._jobs.next(response.jobs);
                    this._jobsTotalResults.next(response.totalResults);

                    return response.jobs;
                }

                throw new Error(response.error);
            })
        );
    }

    /**
     * Searches jobs without updating any shared state (jobs$, filterQuery$, etc.).
     * Use this for isolated lookups such as the timer header dropdown, so that
     * page-level job tables are not affected.
     */
    searchJobsSilent(query: JobSearchQuery): Observable<Job[]> {
        return this._http.get<any>(this._buildSearchQuerySilent(query)).pipe(
            map((response: any) => {
                if (response.success) { return response.jobs as Job[]; }
                throw new Error(response.error);
            })
        );
    }

    /**
     * Lightweight job search via /api/job/jobsearch.
     * Returns only the fields needed for dropdowns (ShortJob).
     */
    searchJobsLightweight(params: { status?: string; searchTerm?: string; resultsPerPage?: number; clientName?: string; jobIds?: number[] }): Observable<ShortJob[]> {
        let url = `${this.originUrl}api/job/jobsearch`;
        const parts: string[] = [];
        if (params.status) { parts.push(`status=${encodeURIComponent(params.status)}`); }
        if (params.searchTerm) { parts.push(`searchTerm=${encodeURIComponent(params.searchTerm)}`); }
        if (params.resultsPerPage != null) { parts.push(`resultsPerPage=${params.resultsPerPage}`); }
        if (params.clientName) { parts.push(`clientName=${encodeURIComponent(params.clientName)}`); }
        if (params.jobIds && params.jobIds.length) { parts.push(`jobIds=${params.jobIds.join(',')}`); }
        if (parts.length) { url += '?' + parts.join('&'); }
        return this._http.get<ShortJob[]>(url);
    }

    /**
     * Returns all live jobs (lightweight), cached after the first fetch.
     * Shared across all job-search component instances.
     */
    getAllLiveJobsCached(): Observable<ShortJob[]> {
        if (this._allLiveJobsCache) {
            return of(this._allLiveJobsCache);
        }
        if (this._allLiveJobsInFlight) {
            return this._allLiveJobsInFlight;
        }
        this._allLiveJobsInFlight = this.searchJobsLightweight({ status: 'live' }).pipe(
            tap(jobs => {
                this._allLiveJobsCache = jobs;
                this._allLiveJobsInFlight = null;
            }),
            shareReplay(1),
        );
        return this._allLiveJobsInFlight;
    }

    /**
     * Distinct display names of clients that have archived jobs (lightweight,
     * cached). Used by the job picker to list clients that have only archived
     * jobs — the jobs themselves still load lazily per client.
     */
    getArchivedJobClientsCached(): Observable<string[]> {
        if (this._archivedClientsCache) {
            return of(this._archivedClientsCache);
        }
        if (this._archivedClientsInFlight) {
            return this._archivedClientsInFlight;
        }
        this._archivedClientsInFlight = this._http.get<string[]>(`${this.originUrl}api/job/archivedclients`).pipe(
            tap(names => {
                this._archivedClientsCache = names;
                this._archivedClientsInFlight = null;
            }),
            shareReplay(1),
        );
        return this._archivedClientsInFlight;
    }

    private _buildSearchQuerySilent(searchQuery: JobSearchQuery): string {
        const base = `${this.originUrl}api/job`;
        let query = `?page=${searchQuery.page}`;

        if (searchQuery.clientId) {
            query += `&clients=${searchQuery.clientId}`;
        } else if (searchQuery.clients?.length) {
            searchQuery.clients.forEach(c => (query += `&clients=${c}`));
        }
        if (searchQuery.userId)       { query += `&userId=${searchQuery.userId}`; }
        if (searchQuery.searchTerm)   { query += `&searchTerm=${encodeURIComponent(searchQuery.searchTerm)}`; }
        if (searchQuery.sortBy)       { query += `&sortBy=${searchQuery.sortBy}&sortDir=${searchQuery.sortDir || 'asc'}`; }
        if (searchQuery.archived)     { query += `&archived=true`; }
        if (searchQuery.active)       { query += `&active=${searchQuery.active}`; }
        if (searchQuery.status)       { query += `&status=${searchQuery.status}`; }
        if (searchQuery.resultsPerPage) { query += `&resultsPerPage=${searchQuery.resultsPerPage}`; }
        // The "silent" search powers the timer job pickers — opt into
        // client-name matching so a user can find a job by typing its client.
        // The jobs listing page uses _buildSearchQuery (without this) so its
        // search stays job-number/title only.
        query += `&includeClient=true`;

        return `${base}${query}`;
    }

    getJob(jobId: number): Observable<Job> {
        return this._http.get<Job>(`${this.originUrl}api/Job/GetJob/${jobId}`);
    }

    getJobByJobNumber(jobNumber: number): Observable<Job> {
        return this._http.get<Job>(
            `${this.originUrl}api/Job/GetJobByJobNumber/${jobNumber}`
        );
    }

    getJobDetails(jobId: number): Observable<JobDetailWithActionStatuses> {
        return this._http.get<JobDetailWithActionStatuses>(
            `${this.originUrl}api/Job/GetJobDetails/${jobId}`
        );
    }

    getJobTypes(): Observable<JobType[]> {
        return this._http
            .get<JobType[]>(`${this.originUrl}api/Job/GetJobTypes`)
            .pipe(tap((jobTypes) => this._jobTypes.next(jobTypes)));
    }

    getAllJobTypes(): Observable<JobType[]> {
        return this._http
            .get<JobType[]>(`${this.originUrl}api/Job/GetAllJobTypes`);
    }

    getJiraTest(): Observable<any> {
        let username = "jimlynn@gmail.com";
        let password = "ATATT3xFfGF08heyIW4WkKexDO6yjosUHiF2nFbO5eUA35ZSkhQmDUXcpac8QRxdQbI4umKUs0yF-JGMfV1w70dX5ZdsnMnZqg24pGoV67YmDY-3kazfnnHY2wYf5KMce76qhmecCVHXfP4rlAGxQ6J-vjJA1Fp4_Difi7734n05P-_DEfT5xhs=952B9E6A";
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Basic ' + btoa(username + ':' + password)
        });
        //headers.append("Authorization", "Basic " + btoa(username + ":" + password));
        console.log(headers);
        return this._simplehttp.get<any>(`https://visarcdev.atlassian.net/rest/api/3/issue/HYG-673?fields=creator,created`, { headers: headers });
    }

    getJiraName(url: string): Observable<JiraNameResult> {
        console.log('getJiraName', url);
        // make an http call to api/Job/GetJiraName with a query parameter of url
        return this._http.get<JiraNameResult>(`${this.originUrl}api/Job/GetJiraName?url=${url}`);

    }

    getJiraLinksForJob(jobId: number): Observable<JiraLinksForJob> {
        return this._http.get<JiraLinksForJob>(
            `${this.originUrl}api/Job/GetJiraLinksForJob/${jobId}`
        );
    }

    updateJiraLinksForJob(payload: UpdateJiraLinksForJob): Observable<any> {
        return this._http.post(`${this.originUrl}api/Job/UpdateJiraLinksForJob`, payload);
    }

    getJobValue(jobId: number): Observable<JobValue> {
        return this._http.get<JobValue>(
            `${this.originUrl}api/Job/GetJobValue/${jobId}`
        );
    }

    getYearJobValues(jobId: number): Observable<JobValueDataForYear[]> {
        return this._http.get<JobValueDataForYear[]>(
            `${this.originUrl}api/Job/getJobValueDetail/${jobId}`
        );
    }

    /**
     * Links two jobs by their IDs.
     *
     * @param parentId - The ID of the parent job.
     * @param childId - The ID of the child job.
     * @returns An Observable that emits a ResultBase object.
     * This will error if:
     *   Either of the jobs do not exist.
     *   The parent job is already a child of another job
     *   The child job is already a child of another job
     */
    linkJobs(parentId: number, childId: number): Observable<ResultBase> {
        return this._http.post<ResultBase>(
            `${this.originUrl}api/Job/LinkJobs?parentId=${parentId}&childId=${childId}`,
            null
        );
    }

    /**
     * Unlinks a job by its ID. This will remove the link between the job and its parent.
     * You can't unlink a job if it is the parent of another job.
     *
     * @param {number} jobId - The ID of the job to unlink.
     * @returns {Observable<ResultBase>} An observable containing the result of the unlink operation.
     * This will error if:
     *   The job does not exist.
     *   The job is not the child of another job.
     */
    unlinkJob(jobId: number): Observable<ResultBase> {
        return this._http.post<ResultBase>(
            `${this.originUrl}api/Job/UnlinkJob?jobId=${jobId}`,
            null
        );
    }

    /**
     * The specified job should be the child of another job.
     * This will make the specified job the new parent of the linked jobs
     * If there are more than one linked job,all of these will become
     * children of the specified job, as will the former parent.
     *
     * @param {number} jobId - The ID of the job to be made a linked job parent.
     * @returns {Observable<ResultBase>} An observable that emits the result of the operation.
     * This will error if:
     *   The job does not exist.
     *   The job is not the child of another job
     *   The job is already the parent of another job
     */
    makeLinkedJobParent(jobId: number): Observable<ResultBase> {
        return this._http.post<ResultBase>(
            `${this.originUrl}api/Job/MakeLinkedJobParent?jobId=${jobId}`,
            null
        );
    }

    getLinkedJobDetails(jobId: number) {
        return this._http.get<LinkedJob[]>(
            `${this.originUrl}api/Job/GetLinkedJobDetails/${jobId}`
        );
    }

    getLinkedJobDetailsCached(jobId: number): Observable<LinkedJob[]> {
        const cached = this._linkedJobsCache.get(jobId);
        if (cached) return of(cached);

        const inflight = this._linkedJobsInFlight.get(jobId);
        if (inflight) return inflight;

        const req$ = this.getLinkedJobDetails(jobId).pipe(
            tap(list => this._linkedJobsCache.set(jobId, list)),
            finalize(() => this._linkedJobsInFlight.delete(jobId)),
            shareReplay(1)
        );

        this._linkedJobsInFlight.set(jobId, req$);
        return req$;
    }


    getJobTimeValues(query: JobTimeValuesQuery): Observable<JobReportValues[]> {
        let params = new HttpParams();
        if (query.clientId) {
            params = params.append('clientId',query.clientId);
        }
        if (query.searchTerm) {
            params = params.append('searchTerm',query.searchTerm);
        }
        if (query.jobOwnerId) {
            params = params.append('jobOwnerId',query.jobOwnerId);
        }
        if (query.groupId != null) {
            params = params.append('groupId', String(query.groupId));
        }
        if (query.jobType) {
            params = params.append('jobType',query.jobType);
        }
        if (query.groupLinked) {
            params = params.append('groupLinked','true');
        }
        params = params.append('includeArchived', query.includeArchived);
        params = params.append('includeBillable', query.includeBillable);
        params = params.append('reportType', query.reportType);
        if (query.sortBy) {
            params = params.append('sortBy',query.sortBy);
        }
        if (query.sortDir) {
            params = params.append('sortDir',query.sortDir);
        }
        if (query.dateFrom) {
            const formatted = this._datePipe.transform(query.dateFrom, 'yyyy-MM-dd');
            params = params.append('dateFrom', formatted);
        }
        if (query.dateTo) {
            const formatted = this._datePipe.transform(query.dateTo, 'yyyy-MM-dd');
            params = params.append('dateTo', formatted);
        }

        return this._http
            .get<JobReportValues[]>(
                `${this.originUrl}api/Job/GetTogglReport`, { params }
            )
            .pipe(map((r) => r as JobReportValues[]));
    }

    updateJobNotes(jobId: number, notes: string): Observable<{ notes: string }> {
        return this._http.post<{ notes: string }>(
            `${this.originUrl}api/Job/UpdateJobNotes`,
            { jobId, notes }
        );
    }

    addOrUpdateJob(job: Job): Observable<JobUpdateResult> {
        let jobToUpdate = job;

        if (job.jobId) {
            jobToUpdate = new Job();
            jobToUpdate.active = job.active;
            jobToUpdate.archive = job.archive;
            jobToUpdate.clientId = job.clientId;
            jobToUpdate.dateCreated = job.dateCreated;
            jobToUpdate.deliveryDate = job.deliveryDate;
            jobToUpdate.jobActions = [];
            jobToUpdate.jobId = job.jobId;
            jobToUpdate.jobNoOR = job.jobNoOR;
            jobToUpdate.jobOwnerId = job.jobOwnerId;
            jobToUpdate.jobStatus = job.jobStatus;
            jobToUpdate.jobTitle = job.jobTitle;
            jobToUpdate.jobTypeId = job.jobTypeId;
            jobToUpdate.isBillable = job.isBillable;
            jobToUpdate.rocks = job.rocks;
        }

        return this._http
            .post(`${this.originUrl}api/Job/AddOrUpdateJob`, jobToUpdate)
            .pipe(map((r) => r as JobUpdateResult));
    }

    archive(jobId: number, archive: boolean): Observable<JobUpdateResult> {
        return this._http
            .post(`${this.originUrl}api/Job/Archive`, { jobId, archive })
            .pipe(map((r) => r as JobUpdateResult));
    }

    getUserBillableReport(dateFrom: Date | null, dateTo: Date | null, includeNonBillable: boolean, groupName: string, includeVisarc: boolean): Observable<BillableHoursForUserResult> {
        let params = new HttpParams();
        if (dateFrom) {
            const formattedFrom = this._datePipe.transform(dateFrom, 'yyyy-MM-dd');
            params = params.append('dateFrom', formattedFrom);
        }
        if (dateTo) {
            const formattedTo = this._datePipe.transform(dateTo, 'yyyy-MM-dd');
            params = params.append('dateTo', formattedTo);
        }
        if (groupName) {
            params = params.append('groupName', groupName);
        }
        params = params.append('includeNonBillable', includeNonBillable ? 'true' : 'false');
        params = params.append('includeVisarc', includeVisarc ? 'true' : 'false');
        return this._http
            .get<BillableHoursForUserResult>(
                `${this.originUrl}api/Job/GetUserBillableReport`, { params })
                .pipe(map((r) => r as BillableHoursForUserResult));
    }

    getJobTypeBillableReport(dateFrom: Date | null, dateTo: Date | null, includeVisarc: boolean): Observable<BillableHoursForJobTypeResult> {
        let params = new HttpParams();
        if (dateFrom) {
            const formattedFrom = this._datePipe.transform(dateFrom, 'yyyy-MM-dd');
            params = params.append('dateFrom', formattedFrom);
        }
        if (dateTo) {
            const formattedTo = this._datePipe.transform(dateTo, 'yyyy-MM-dd');
            params = params.append('dateTo', formattedTo);
        }
        params = params.append('includeVisarc', includeVisarc ? 'true' : 'false');
        return this._http
            .get<BillableHoursForJobTypeResult>(
                `${this.originUrl}api/Job/GetJobTypeBillableReport`, { params })
                .pipe(map((r) => r as BillableHoursForJobTypeResult));
    }

    changeBillableStatus(jobId: number, isBillable: boolean): Observable<ResultBase> {
        return this._http.post<ResultBase>(
            `${this.originUrl}api/Job/ChangeBillableStatus`,
            { jobId, isBillable }
        );
    }

    notifyBillableChanged(jobId: number, isBillable: boolean): void {
        this._billableStatusChanged$.next({ jobId, isBillable });
    }

    deleteJob(jobId: number): Observable<ResultBase> {
        return this._http.post<ResultBase>(`${this.originUrl}api/Job/DeleteJob?jobId=${jobId}`, null);
    }

    getClientBillableReport(dateFrom: Date | null, dateTo: Date | null, groupClients: boolean, includeVisarc: boolean, groupName: string): Observable<BillableHoursForClientResult> {
        let params = new HttpParams();
        if (dateFrom) {
            const formattedFrom = this._datePipe.transform(dateFrom, 'yyyy-MM-dd');
            params = params.append('dateFrom', formattedFrom);
        }
        if (dateTo) {
            const formattedTo = this._datePipe.transform(dateTo, 'yyyy-MM-dd');
            params = params.append('dateTo', formattedTo);
        }
        if (groupName) {
            params = params.append('groupName', groupName);
        }
        params = params.append('includeVisarc', includeVisarc ? 'true' : 'false');
        params = params.append('groupClients', groupClients ? 'true' : 'false');
        return this._http
            .get<BillableHoursForClientResult>(
                `${this.originUrl}api/Job/GetClientBillableReport`, { params })
                .pipe(map((r) => r as BillableHoursForClientResult));
    }

    reset(): void {
        this._jobs.next([]);
        this._jobsTotalResults.next(0);
    }

    /**
     * Fetches all unique job owner IDs (users who own at least one job).
     * Optionally filters by a single client ID or an array of client IDs.
     * Results for "all owners" (no client filter) are cached in the service.
     */
    getJobOwners(clientId?: number, clientIds?: number[]): Observable<{ userIds: string[] }> {
        const isAllOwners = !clientId && (!clientIds || clientIds.length === 0);

        // Return cached result immediately if available (for "all owners" query)
        if (isAllOwners && this._allJobOwnersCache) {
            return of({ userIds: this._allJobOwnersCache });
        }

        // Return in-flight request if one exists (for "all owners" query)
        if (isAllOwners && this._allJobOwnersInFlight) {
            return this._allJobOwnersInFlight;
        }

        // If we have multiple client IDs (client group), make parallel requests and combine results
        if (clientIds && clientIds.length > 0 && !clientId) {
            const requests$ = clientIds.map(id =>
                this._http.get<{ ownerIds: string[] }>(`${this.originUrl}api/job/ownerids/${id}`)
            );

            return forkJoin(requests$).pipe(
                map((responses) => {
                    // Combine all owner IDs and deduplicate
                    const allOwnerIds = new Set<string>();
                    responses.forEach(response => {
                        (response.ownerIds || []).forEach(id => allOwnerIds.add(id));
                    });
                    return { userIds: Array.from(allOwnerIds) };
                })
            );
        }

        // Use the dedicated endpoint for single client or all owners
        let url = `${this.originUrl}api/job/ownerids`;
        if (clientId) {
            url += `/${clientId}`;
        }

        const request$ = this._http.get<{ ownerIds: string[] }>(url).pipe(
            map((response) => {
                // Map ownerIds to userIds for consistency with the component
                return { userIds: response.ownerIds || [] };
            }),
            tap((result) => {
                // Cache "all owners" result
                if (isAllOwners) {
                    this._allJobOwnersCache = result.userIds;
                    this._allJobOwnersInFlight = null;
                }
            }),
            finalize(() => {
                if (isAllOwners) {
                    this._allJobOwnersInFlight = null;
                }
            }),
            shareReplay(1)
        );

        // Track in-flight request for "all owners" query
        if (isAllOwners) {
            this._allJobOwnersInFlight = request$;
        }

        return request$;
    }

    /**
     * Returns cached job owners if available, or null if not yet loaded.
     * Use this for instant access without triggering a fetch.
     */
    getCachedJobOwners(): string[] | null {
        return this._allJobOwnersCache;
    }

    private _buildSearchQuery(searchQuery?: JobSearchQuery): string {
        var baseUrl = `${this.originUrl}api/job`;
        if (!searchQuery) return baseUrl;

        var query = `?page=${searchQuery.page}`;

        if (searchQuery.clientId) {
            query += `&clients=${searchQuery.clientId}`;
        } else {
            if (searchQuery.clients && searchQuery.clients.length > 0) {
                searchQuery.clients.forEach(
                    (client) => (query += `&clients=${client}`)
                );
            }
        }

        if (searchQuery.userId) {
            query += `&userId=${searchQuery.userId}`;
        }

        if (searchQuery.searchTerm) {
            query += `&searchTerm=${encodeURIComponent(
                searchQuery.searchTerm
            )}`;
        }

        if (searchQuery.sortBy) {
            let direction = searchQuery.sortDir || 'asc';
            query += `&sortBy=${searchQuery.sortBy}&sortDir=${direction}`;
        }

        if (searchQuery.archived) {
            query += `&archived=true`;
        }

        if (searchQuery.status) {
            query += `&status=${searchQuery.status}`;
        }

        if (searchQuery.active && searchQuery.active !== 'All') {
            query += `&active=${searchQuery.active}`;
        }

        if (searchQuery.resultsPerPage) {
            query += `&resultsPerPage=${searchQuery.resultsPerPage}`;
        }

        if (!_.isEqual(this._filterQuery.value, searchQuery)) {
            this._filterQuery.next(searchQuery);
        }

        return `${baseUrl}${query}`;
    }

    private _mapJob(job: any): Job {
        let result: Job = job;
        return result;
    }

    initNewJobType(): Observable<JobType> {
        return of({ jobTypeId: 0, description: '', active: true } as JobType);
    }

    getJobTypeToEdit(jobTypeId: number) : Observable<JobTypeToEditResult> {
        return this._http.get<JobTypeToEditResult>(
            `${this.originUrl}api/Job/GetJobTypeToEdit/${jobTypeId}`
        );  
    }

    addOrUpdateJobType(jobType: JobType): Observable<AddOrUpdateJobTypeResult> {
        return this._http
            .post<AddOrUpdateJobTypeResult>(`${this.originUrl}api/Job/AddOrUpdateJobType`, jobType)
            .pipe(tap((jobTypes) => this.getJobTypes().subscribe()));
    }        
}
